import { AsyncPipe, NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { SearchComponent } from "@components/search/search.component";
import { chain } from "lodash";
import { BehaviorSubject, forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { ProduktionsstatusPipe } from "~app/common/pipes/produktionsstatus.pipe";
import { MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { AnvandareService } from "~app/common/services/anvandare.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ArbetsorderService, GetArbetsorderAlertsQueryResult } from "~app/features/planera/services/arbetsorder.service";
import { GetVirkeskopAlertsQueryResult, VirkeskopService } from "~app/features/virkeskop/services/virkeskop.service";
import { IHandelseViewModel, IVirkeskopshandelseArbetsorder, SeverityFilterViewModel } from "./handelser.models";

@Component({
    selector: "rask-start-handelser",
    templateUrl: "./handelser.component.html",
    styleUrls: ["./handelser.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SearchComponent,
        CheckboxComponent,
        ListComponent,
        ListColumnDirective,
        NgClass,
        RouterLink,
        AsyncPipe
    ]
})
export class HandelserComponent implements OnInit {
    filteredHandelser$ = new BehaviorSubject<IHandelseViewModel[]>([]);
    filters: SeverityFilterViewModel[] = [
        new SeverityFilterViewModel(MessageSeverity.Critical, "Kritiskt fel", true),
        new SeverityFilterViewModel(MessageSeverity.Error, "Fel", true),
        new SeverityFilterViewModel(MessageSeverity.Warning, "Varning", false),
        new SeverityFilterViewModel(MessageSeverity.Update, "Uppdatering krävs", true),
        new SeverityFilterViewModel(MessageSeverity.Info, "Information", false)
    ];
    form = new UntypedFormGroup({
        sokTypeAhead: new UntypedFormControl("")
    });
    handelser: IHandelseViewModel[] = [];
    @Output() numberOfErrors: EventEmitter<number> = new EventEmitter<number>();
    relatedArbetsordrar: IVirkeskopshandelseArbetsorder[] = [];

    private readonly anvandareService = inject(AnvandareService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly produktionsstatusPipe = inject(ProduktionsstatusPipe);
    private readonly virkeskopService = inject(VirkeskopService);

    filter() {
        const selectedValues = this.filters.filter(x => x.selected).map(x => x.severity);
        const match = x => selectedValues.includes(x.severity);
        this.filteredHandelser$.next(this.handelser.filter(match));
    }

    getHandelser(userId?: number) {
        const mapArbetsorder = (source: GetArbetsorderAlertsQueryResult[]): any[] =>
            source.map(x => ({
                source: "ao",
                id: x.arbetsorderId,
                linkName: x.namn,
                produktionsstatus: x.produktionsstatus,
                scheduledDate: x.scheduledDate,
                alerts: x.alerts
            }));

        const mapVirkeskop = (source: GetVirkeskopAlertsQueryResult[]): any[] => source.map(x => ({
            source: "vk",
            id: x.virkeskopsavtalId,
            linkName: x.namn,
            relatedArbetsordrar: x.arbetsorderList,
            subEntityId: x.subEntityId,
            subEntityLinkName: x.subEntityLinkName,
            subEntityType: x.subEntityType,
            alerts: x.alerts
        }));
        const errors$ = forkJoin({
            ao: this.arbetsorderService.getArbetsorderErrors(userId ?? 0)
                .pipe(map((res) => mapArbetsorder(res))),
            vk: this.virkeskopService.getVirkeskopErrors(userId ?? 0)
                .pipe(map((res) => mapVirkeskop(res)))
        });

        errors$
            .pipe(map((res) => [
                ...res.ao,
                ...res.vk
            ]))
            .subscribe(res => this.initHandelser(res));
    }

    messageIconClass(severity: MessageSeverity): string {
        switch (severity) {
            case MessageSeverity.Critical:
                return "far fa-exclamation-triangle error";
            case MessageSeverity.Error:
                return "far fa-exclamation-circle error";
            case MessageSeverity.Warning:
                return "far fa-exclamation-square warning";
            case MessageSeverity.Update:
                return "far fa-sync";
            case MessageSeverity.Info:
                return "far fa-info-circle";
            default:
                return "";
        }
    }

    ngOnInit(): void {
        this.getHandelser();
    }

    searchAnvandareTypeAhead = (searchString: string) => this.anvandareService.searchForAnvandareTypeahead(searchString);

    protected getLinkName(linkname: string, subEntityLinkName: string): string {
        return `${linkname}/${subEntityLinkName}`;
    }

    private initHandelser(data: any[]) {
        this.handelser = chain(data)
            .map(x => x.alerts.map(al => ({
                isArbetsorder: x.source === "ao",
                relatedArbetsordrar: x.relatedArbetsordrar,
                id: x.id,
                linkName: x.linkName,
                subEntityId: al.subEntityId,
                subEntityLinkName: al.subEntityLinkName,
                subEntityType: al.subEntityType,
                message: al.message,
                errorDate: DateUtils.formatDateString(al.occured, "YYYY-MM-DD hh:mm"),
                aoStatus: this.produktionsstatusPipe.transform(x.produktionsstatus),
                scheduled: DateUtils.shortDateString(x.scheduledDate),
                severity: al.severity
            })))
            .flatten()
            .value();

        this.filters.forEach(x => x.antal = this.handelser.filter(x1 => x1.severity === x.severity).length);
        this.filter();
        this.numberOfErrors.emit(this.handelser.length);
    }
}
